import { useState } from "react";
import styles from "./Settings.module.css";

function Toggle({ checked, onChange }) {
  return (
    <label className={styles.toggle}>
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className={styles.slider} />
    </label>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHead}>
        <span className={styles.sectionIcon}>{icon}</span>
        <h2 className={styles.sectionTitle}>{title}</h2>
      </div>
      <div className={styles.sectionBody}>{children}</div>
    </div>
  );
}

function Row({ label, sub, right }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowLeft}>
        <span className={styles.rowLabel}>{label}</span>
        {sub && <span className={styles.rowSub}>{sub}</span>}
      </div>
      <div className={styles.rowRight}>{right}</div>
    </div>
  );
}

export default function Settings() {
  const [notifs, setNotifs] = useState({
    matchReminders: true,
    resultsAlerts: true,
    leaderboardUpdates: false,
    weeklyDigest: true,
    promoOffers: false,
  });
  const toggleNotif = (k) => setNotifs((p) => ({ ...p, [k]: !p[k] }));

  const [favTeam, setFavTeam] = useState("Arsenal");
  const [theme, setTheme] = useState("Dark");

  const [privacy, setPrivacy] = useState({
    publicProfile: false,
    showPredictions: true,
    showStats: true,
  });
  const togglePrivacy = (k) => setPrivacy((p) => ({ ...p, [k]: !p[k] }));

  const [savedMsg, setSavedMsg] = useState("");
  const handleSave = () => {
    setSavedMsg("Changes saved!");
    setTimeout(() => setSavedMsg(""), 2500);
  };

  return (
    <div className={styles.page}>
      <div className={styles.glowBar} />

      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Settings</h1>
          <p className={styles.subtitle}>
            Manage your account, preferences and notifications
          </p>
        </div>
        <div className={styles.headerRight}>
          {savedMsg && <span className={styles.savedToast}>{savedMsg}</span>}
          <button className={styles.saveBtn} onClick={handleSave}>
            Save Changes
          </button>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.col}>
          <Section icon="" title="Account">
            <div className={styles.accountMode}>
              <Row
                label="Username"
                sub="Shown on leaderboard and predictions"
                right={
                  <input
                    className={styles.textInput}
                    defaultValue="The Oracle"
                  />
                }
              />
              <Row
                label="Email"
                sub="Used for notifications and recovery"
                right={
                  <input
                    className={styles.textInput}
                    defaultValue="oracle@example.com"
                    type="email"
                  />
                }
              />
              <Row
                label="Password"
                sub="Update your account password for better security"
                right={
                  <button className={styles.ghostBtn}>Change Password</button>
                }
              />
            </div>
          </Section>
          <Section icon="" title="Preferences">
            <Row
              label="Favourite Team"
              right={
                <select
                  className={styles.select}
                  value={favTeam}
                  onChange={(e) => setFavTeam(e.target.value)}
                >
                  {[
                    "Arsenal",
                    "Chelsea",
                    "Real Madrid",
                    "Barcelona",
                    "Bayern",
                    "PSG",
                    "Dortmund",
                    "Man City",
                    "Juventus",
                    "AC Milan",
                  ].map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              }
            />

            <Row
              label="Theme"
              right={
                <div className={styles.segmented}>
                  {["Dark", "Light", "System"].map((t) => (
                    <button
                      key={t}
                      className={`${styles.segBtn} ${theme === t ? styles.segActive : ""}`}
                      onClick={() => setTheme(t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              }
            />
          </Section>
        </div>

        <div className={styles.col}>
          <Section icon="" title="Notifications">
            {[
              {
                key: "matchReminders",
                label: "Match Reminders",
                sub: "Get notified 1h before kick-off",
              },
              {
                key: "resultsAlerts",
                label: "Results & Scores",
                sub: "Instant alerts when matches end",
              },
              {
                key: "leaderboardUpdates",
                label: "Leaderboard Changes",
                sub: "When your rank changes",
              },
              {
                key: "weeklyDigest",
                label: "Weekly Digest",
                sub: "Summary of predictions every Monday",
              },
            ].map((n) => (
              <Row
                key={n.key}
                label={n.label}
                sub={n.sub}
                right={
                  <Toggle
                    checked={notifs[n.key]}
                    onChange={() => toggleNotif(n.key)}
                  />
                }
              />
            ))}
          </Section>

          <Section icon="" title="Privacy">
            <Row
              label="Public Profile"
              sub="Anyone can view your predictions"
              right={
                <Toggle
                  checked={privacy.publicProfile}
                  onChange={() => togglePrivacy("publicProfile")}
                />
              }
            />
            <Row
              label="Show My Predictions"
              sub="Visible on match detail pages"
              right={
                <Toggle
                  checked={privacy.showPredictions}
                  onChange={() => togglePrivacy("showPredictions")}
                />
              }
            />
            <Row
              label="Show My Stats"
              sub="Accuracy & XP visible to others"
              right={
                <Toggle
                  checked={privacy.showStats}
                  onChange={() => togglePrivacy("showStats")}
                />
              }
            />
          </Section>

          <Section icon="" title="Danger Zone">
            <div className={styles.dangerBox}>
              <div className={styles.dangerRow}>
                <div>
                  <p className={styles.dangerLabel}>Reset Predictions</p>
                  <p className={styles.dangerSub}>
                    Permanently delete all your prediction history
                  </p>
                </div>
                <button className={styles.dangerBtn}>Reset</button>
              </div>
              <div className={styles.dangerDivider} />
              <div className={styles.dangerRow}>
                <div>
                  <p className={styles.dangerLabel}>Delete Account</p>
                  <p className={styles.dangerSub}>
                    Permanently remove your account and all data
                  </p>
                </div>
                <button
                  className={`${styles.dangerBtn} ${styles.dangerBtnRed}`}
                >
                  Delete
                </button>
              </div>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
